/* eslint-disable import/prefer-default-export */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const STORAGE_KEY = "routeHistory";
const MAX_HISTORY = 50;
const DEFAULT_BACK_ROUTE = "/shipments";

/**
 * Shipment detail pages (tracking, quotes, billing, etc.) live under `/shipment/:id/*`.
 * List routes like `/shipments` must not match this check.
 */
export function isShipmentDetailRoute(route: string): boolean {
  const pathname = route.split("?")[0];
  return pathname.startsWith("/shipment/");
}

/**
 * Returns the most recent route the user was on before entering a shipment detail page.
 * Falls back to `/shipments` when history has no prior entry page (e.g. direct link).
 */
export function getLastEntryRoute(history: string[]): string {
  const lastEntryRoute = history
    .slice()
    .reverse()
    .find((route) => !isShipmentDetailRoute(route));

  return lastEntryRoute || DEFAULT_BACK_ROUTE;
}

export function useRouteHistory() {
  const location = useLocation();

  useEffect(() => {
    const currentRoute = location.pathname + location.search;

    const stored = sessionStorage.getItem(STORAGE_KEY);
    const history = stored ? JSON.parse(stored) : [];

    // Avoid duplicates when React re-renders
    if (history[history.length - 1] !== currentRoute) {
      history.push(currentRoute);

      // Limit history size
      if (history.length > MAX_HISTORY) {
        history.shift();
      }
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(history));
    }
  }, [location]);

  const history = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "[]");
  const lastEntryRoute = getLastEntryRoute(history);

  return { history, lastEntryRoute };
}
